import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StatusBadge } from './Insider';
import PosterPlaceholder from './PosterPlaceholder';
import LoginModal from './LoginModal';

const DEFAULT_API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Order sections so the most "in development" stuff leads; merge + dedupe into
// one row for the front page.
const SECTION_ORDER = ['now_filming', 'recently_announced', 'recently_greenlit', 'recently_updated'];

function mergeSections(sections, limit) {
  const seen = new Set();
  const merged = [];
  for (const key of SECTION_ORDER) {
    for (const p of sections[key] || []) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      merged.push(p);
      if (merged.length >= limit) return merged;
    }
  }
  return merged;
}

// A front-page carousel of VideBits Insider projects (film/TV in development),
// linking through to the Insider project pages. Renders nothing if there's no
// data yet, so it never shows an empty row on the landing page.
export default function InsiderCarousel({ apiBase = DEFAULT_API, limit = 20 }) {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  // Where the user was headed when prompted to sign in, so we can take them
  // there once they're authenticated.
  const [pendingPath, setPendingPath] = useState(null);

  const promptSignIn = (path) => {
    setPendingPath(path);
    setShowLogin(true);
  };

  const onSignedIn = () => {
    if (pendingPath) navigate(pendingPath);
    setPendingPath(null);
  };

  const load = useCallback(async () => {
    // The radar is a public taster of Insider, so it loads for everyone.
    try {
      const resp = await fetch(`${apiBase}/api/insider/radar?limit=${limit}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error(`Request failed (${resp.status})`);
      const data = await resp.json();
      setItems(mergeSections(data.sections || {}, limit));
    } catch {
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, limit, token]);

  useEffect(() => { load(); }, [load]);

  // Don't render an empty row once loaded with nothing.
  if (!isLoading && items.length === 0) return null;

  return (
    <div className="bg-white py-10 px-4 border-t border-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-baseline gap-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-videbits-500" />
              On the Radar
            </h2>
            <span className="text-xs text-gray-400">Film &amp; TV in development — VideBits Insider</span>
          </div>
          {isAuthenticated ? (
            <Link to="/insider" className="inline-flex items-center gap-0.5 text-sm font-medium text-videbits-600 hover:text-videbits-800 transition-colors">
              Insider <ChevronRight className="w-4 h-4" />
            </Link>
          ) : (
            <button type="button" onClick={() => promptSignIn('/insider')} className="inline-flex items-center gap-0.5 text-sm font-medium text-videbits-600 hover:text-videbits-800 transition-colors">
              Insider <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-28 h-44 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {items.map((p) => {
              const inner = (
                <>
                  <div className="w-28 h-40 rounded-xl overflow-hidden bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all duration-200">
                    {p.poster_url ? (
                      <img
                        src={p.poster_url}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <PosterPlaceholder />
                    )}
                  </div>
                  <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight min-h-[1.875rem] mb-1">{p.title}</p>
                  <StatusBadge status={p.status} />
                </>
              );
              // Public taster: cards show for everyone, but clicking through to the
              // gated Insider pages prompts sign in when signed out.
              return isAuthenticated ? (
                <Link key={p.id} to={`/insider/project/${p.id}`} className="flex-shrink-0 w-28 group">
                  {inner}
                </Link>
              ) : (
                <button key={p.id} type="button" onClick={() => promptSignIn(`/insider/project/${p.id}`)} className="flex-shrink-0 w-28 group text-left">
                  {inner}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        initialMode="login"
        onSuccess={onSignedIn}
      />
    </div>
  );
}
